import { UploadOutlined } from '@ant-design/icons';
import { Image, Upload, message } from 'antd';
import { UploadFile } from 'antd/es/upload/interface';
import { useCustomUpload } from 'hooks/business/useUploadResource';
import { useTranslate } from '@tolgee/react';
import { useState } from 'react';

interface RefuseUploadImageProps {
  value?: any;
  onChange?: (value: any) => void;
  accepts?: string[];
  countLimit: number;
  sizeLimit: number;
  internalStoreId: string;
  reqSource: string;
}

const getPreviewUrl = (file: UploadFile): string | undefined =>
  file.url || file.thumbUrl || file?.response?.data?.url;

export const RefuseUploadImage = (props: RefuseUploadImageProps) => {
  const {
    value = [],
    accepts = ['jpg', 'jpeg', 'png'],
    countLimit,
    sizeLimit,
    internalStoreId,
    reqSource,
    onChange,
  } = props;
  const { t } = useTranslate();
  const customUpload = useCustomUpload();
  const [preview, setPreview] = useState<{ visible: boolean; current: number }>({
    visible: false,
    current: 0,
  });

  const previewableFiles: UploadFile[] = value.filter((file: UploadFile) => !!getPreviewUrl(file));

  return (
    <div>
      <Upload
        listType="picture-card"
        accept={accepts.map((_) => `.${_}`).join(',')}
        fileList={value}
        onChange={({ fileList }) => onChange?.(fileList)}
        onPreview={(file) => {
          const current = previewableFiles.findIndex((item) => item.uid === file.uid);
          if (current >= 0) {
            setPreview({ visible: true, current });
          }
        }}
        beforeUpload={(file) => {
          if (file.size > sizeLimit * 1024 * 1024) {
            message.warn(`${sizeLimit}M`);

            return Upload.LIST_IGNORE;
          }

          return Promise.resolve(file);
        }}
        customRequest={(props) =>
          customUpload(props, {
            storeId: internalStoreId,
            fileType: 'IMAGE',
            reqSource,
          })
        }
      >
        {(value?.length ?? 0) < countLimit && (
          <div>
            <UploadOutlined />
            <div>{t('phrase.上传图片')}</div>
          </div>
        )}
      </Upload>
      <div className="text-xs text-[var(--fett-text-tertiary)]">
        {t('ViewRefund.refuseUploadImageTips', {
          x: countLimit,
          y: sizeLimit,
          z: accepts.map((_) => _.toUpperCase()).join('/'),
        })}
      </div>
      {/* 隐藏的 PreviewGroup 仅用于承载受控预览（支持左右切换），查看器本身渲染在 portal 中 */}
      <div style={{ display: 'none' }}>
        <Image.PreviewGroup
          preview={{
            visible: preview.visible,
            current: preview.current,
            onVisibleChange: (visible) => setPreview((prev) => ({ ...prev, visible })),
          }}
        >
          {previewableFiles.map((file) => (
            <Image key={file.uid} src={getPreviewUrl(file)} />
          ))}
        </Image.PreviewGroup>
      </div>
    </div>
  );
};

export const useRefuseUploadImageValidate = (optional = false) => {
  const { t } = useTranslate();

  return (fileList: UploadFile<any>[]) => {
    if (!optional && (!fileList || fileList.length === 0)) {
      return t('phrase.请上传图片');
    }

    if (fileList && fileList.some(({ status }) => status !== 'success' && status !== 'done')) {
      return t('phrase.存在未上传成功的图片/视频，请删除后重新上传', {
        text: t('phrase.typeImage'),
      });
    }

    return undefined;
  };
};
